import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import UserProfile from './UserProfile';
import Footer from './Footer';
import VideoEditor from './VideoEditor';
import VideoPreview from './VideoPreview';
import VideoHistory from './VideoHistory';
import { useAuth } from '../contexts/AuthContext';
import './FaceSwapHeader.css';
import './ImageToVideoPage.css';
// Template placeholder images
import imgUrban from '../assets/images/templates/urban-fashion.svg';
import imgAbstract from '../assets/images/templates/abstract-art.svg';
import imgSpace from '../assets/images/templates/space-scene.svg';
import imgUnderwater from '../assets/images/templates/underwater.svg';
import imgGolden from '../assets/images/templates/golden-hour.svg';
import imgNeon from '../assets/images/templates/neon-city.svg';
import imgNature from '../assets/images/templates/nature-zen.svg';
import imgTimeLapse from '../assets/images/templates/time-lapse.svg';
import imgVintage from '../assets/images/templates/vintage-film.svg';
import imgAction from '../assets/images/templates/action.svg';
import imgPortrait from '../assets/images/templates/portrait.svg';
import imgLandscape from '../assets/images/templates/landscape.svg';
import imgBlackWhite from '../assets/images/templates/blackwhite.svg';
import imgSunset from '../assets/images/templates/sunset.svg';
import imgRain from '../assets/images/templates/rain.svg';
import imgZoom from '../assets/images/templates/zoom.svg';
import imgCyberpunk from '../assets/images/templates/cyberpunk.svg';
import imgDream from '../assets/images/templates/dream.svg';
import imgDesert from '../assets/images/templates/desert.svg';
import imgAurora from '../assets/images/templates/aurora.svg';
import imgForest from '../assets/images/templates/forest.svg';
import imgFire from '../assets/images/templates/fire.svg';
import imgIce from '../assets/images/templates/ice.svg';
import imgNight from '../assets/images/templates/night.svg';
import imgCandy from '../assets/images/templates/candy.svg';
import imgStorm from '../assets/images/templates/storm.svg';
import imgSlowMotion from '../assets/images/templates/slowmotion.svg';

const ImageToVideoPage: React.FC = () => {
  const { isLoggedIn, isLoading } = useAuth();
  const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [videoSettings, setVideoSettings] = useState<{
    prompt: string;
    model: string;
    duration: string;
    resolution: string;
    templateName?: string;
  }>({
    prompt: '',
    model: 'Wan 2.5 Fast',
    duration: '5s',
    resolution: '720p'
  });

  const templatesRef = useRef<HTMLDivElement>(null);

  // Masonry layout effect
  useEffect(() => {
    const layoutMasonry = () => {
      if (!templatesRef.current) return;
      
      const container = templatesRef.current;
      const items = Array.from(container.children) as HTMLElement[];
      
      // Determine column count based on screen width
      const containerWidth = container.offsetWidth;
      let columnCount = 5;
      if (containerWidth < 480) {
        columnCount = 2;
      } else if (containerWidth < 768) {
        columnCount = 3;
      } else if (containerWidth < 1440) {
        columnCount = 4;
      }
      
      const gap = 12; // 0.75rem = 12px
      const columnWidth = (containerWidth - gap * (columnCount - 1)) / columnCount;
      
      // Initialize column heights
      const columnHeights = new Array(columnCount).fill(0);
      
      items.forEach((item) => {
        // Find the shortest column
        const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
        
        // Position the item (add 8px offset for hover effect)
        const left = shortestColumnIndex * (columnWidth + gap);
        const top = columnHeights[shortestColumnIndex] + 8;
        
        item.style.position = 'absolute';
        item.style.left = `${left}px`;
        item.style.top = `${top}px`;
        item.style.width = `${columnWidth}px`;
        
        // Update column height
        columnHeights[shortestColumnIndex] += item.offsetHeight + gap;
      });
      
      // Set container height
      container.style.height = `${Math.max(...columnHeights)}px`;
    };

    // Layout on mount and when images load
    const timer = setTimeout(layoutMasonry, 100);
    const timer2 = setTimeout(layoutMasonry, 500); // Give images time to load
    window.addEventListener('resize', layoutMasonry);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      window.removeEventListener('resize', layoutMasonry);
    };
  }, []);

  const templates = [
    {
      id: 'into-the-eye',
      name: 'Into the Eye',
      prompt: 'Into the Eye - A mesmerizing journey through surreal landscapes',
      video: '/templates/assets/videos/Into the Eye_Hailuo 02.mp4',
      image: '/templates/assets/images/Into the Eye.png',
      pattern: 'eye',
      isNew: true,
      isVideo: true,
      model: 'Minimax Hailuo 02'
    },
    {
      id: 'mystification',
      name: 'Mystification',
      prompt: 'Mystification - Enchanting and mysterious visual experience',
      video: '/templates/assets/videos/Mystification_Hailuo 02.mp4',
      image: '/templates/assets/images/Mystification.png',
      pattern: 'mystical',
      isNew: true,
      isVideo: true,
      model: 'Minimax Hailuo 02'
    },
    {
      id: 'woman-street',
      name: 'Urban Fashion',
      prompt: 'Urban street fashion, cinematic walk, natural lighting, shallow depth of field',
      image: imgUrban,
      pattern: 'city',
      isNew: true
    },
    {
      id: 'abstract-smoke',
      name: 'Abstract Art',
      prompt: 'Abstract smoke effect, fluid motion, artistic style, dramatic lighting',
      image: imgAbstract,
      pattern: 'smoke',
      isNew: true
    },
    {
      id: 'astronaut',
      name: 'Space Scene',
      prompt: 'Astronaut in space, floating motion, dramatic lighting, cinematic feel',
      image: imgSpace,
      pattern: 'space',
      isNew: true
    },
    {
      id: 'underwater',
      name: 'Underwater',
      prompt: 'Underwater scene, smooth floating motion, natural lighting, serene atmosphere',
      image: imgUnderwater,
      pattern: 'waves'
    },
    {
      id: 'storm',
      name: 'Storm Effect',
      prompt: 'Storm clouds, dramatic weather, dynamic movement, cinematic atmosphere',
      image: imgStorm,
      pattern: 'storm'
    },
    {
      id: 'golden-hour',
      name: 'Golden Hour',
      prompt: 'Golden hour lighting, warm tones, soft glow, cinematic depth of field',
      image: imgGolden,
      pattern: 'sunset'
    },
    {
      id: 'neon-city',
      name: 'Neon City',
      prompt: 'Neon city lights, cyberpunk style, reflections, dynamic camera movement',
      image: imgNeon,
      pattern: 'neon'
    },
    {
      id: 'nature-zen',
      name: 'Nature Zen',
      prompt: 'Peaceful nature scene, gentle motion, natural lighting, calm atmosphere',
      image: imgNature,
      pattern: 'forest'
    },
    {
      id: 'time-lapse',
      name: 'Time Lapse',
      prompt: 'Beautiful time lapse effect, smooth motion, vibrant colors, dynamic lighting',
      image: imgTimeLapse,
      pattern: 'clouds'
    },
    {
      id: 'slow-motion',
      name: 'Slow Motion',
      prompt: 'Ultra slow motion, high frame rate, dramatic reveal, cinematic style',
      image: imgSlowMotion,
      pattern: 'blur'
    },
    {
      id: 'vintage',
      name: 'Vintage Film',
      prompt: 'Vintage film style, grain texture, warm tones, nostalgic feel',
      image: imgVintage,
      pattern: 'grain'
    },
    {
      id: 'action',
      name: 'Action Shot',
      prompt: 'High-energy action, dynamic camera, motion blur, intense atmosphere',
      image: imgAction,
      pattern: 'speed'
    },
    {
      id: 'portrait',
      name: 'Portrait Mode',
      prompt: 'Portrait style, shallow depth of field, natural lighting, cinematic look',
      image: imgPortrait,
      pattern: 'focus'
    },
    {
      id: 'landscape',
      name: 'Landscape',
      prompt: 'Epic landscape shot, wide angle, dramatic sky, golden hour lighting',
      image: imgLandscape,
      pattern: 'mountains'
    },
    {
      id: 'black-white',
      name: 'Black & White',
      prompt: 'Black and white cinematography, high contrast, dramatic lighting',
      image: imgBlackWhite,
      pattern: 'contrast'
    },
    {
      id: 'sunset',
      name: 'Sunset Glow',
      prompt: 'Sunset silhouette, warm tones, dramatic sky, peaceful atmosphere',
      image: imgSunset,
      pattern: 'glow'
    },
    {
      id: 'rain',
      name: 'Rain Effect',
      prompt: 'Rain scene, moody atmosphere, reflections, cinematic depth',
      image: imgRain,
      pattern: 'drops'
    },
    {
      id: 'zoom',
      name: 'Dolly Zoom',
      prompt: 'Dolly zoom effect, dramatic perspective, cinematic technique',
      image: imgZoom,
      pattern: 'spiral'
    },
    {
      id: 'cyberpunk',
      name: 'Cyberpunk',
      prompt: 'Cyberpunk aesthetic, neon lights, futuristic cityscape, high tech atmosphere',
      image: imgCyberpunk,
      pattern: 'city'
    },
    {
      id: 'dream',
      name: 'Dreamscape',
      prompt: 'Dreamy atmosphere, soft focus, ethereal lighting, surreal mood',
      image: imgDream,
      pattern: 'clouds'
    },
    {
      id: 'desert',
      name: 'Desert',
      prompt: 'Desert landscape, golden sand dunes, vast horizon, warm sunlight',
      image: imgDesert,
      pattern: 'dunes'
    },
    {
      id: 'aurora',
      name: 'Aurora',
      prompt: 'Northern lights, aurora borealis, magical sky, ethereal colors',
      image: imgAurora,
      pattern: 'aurora'
    },
    {
      id: 'forest',
      name: 'Forest',
      prompt: 'Deep forest, tall trees, natural lighting, peaceful atmosphere',
      image: imgForest,
      pattern: 'trees'
    },
    {
      id: 'fire',
      name: 'Fire',
      prompt: 'Fire effect, warm glow, dynamic flames, dramatic lighting',
      image: imgFire,
      pattern: 'flames'
    },
    {
      id: 'ice',
      name: 'Ice Crystal',
      prompt: 'Ice crystals, frozen texture, cool tones, winter atmosphere',
      image: imgIce,
      pattern: 'crystal'
    },
    {
      id: 'night',
      name: 'Night Sky',
      prompt: 'Night sky, stars, moonlight, serene atmosphere',
      image: imgNight,
      pattern: 'stars'
    },
    {
      id: 'candy',
      name: 'Candy Color',
      prompt: 'Candy colors, sweet tones, playful mood, vibrant atmosphere',
      image: imgCandy,
      pattern: 'candy'
    }
  ];

  const handleVideoGenerate = (settings: any) => {
    setVideoSettings(settings);
    setIsGenerating(true);
    
    // 模拟视频生成过程
    setTimeout(() => {
      setGeneratedVideo('https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4');
      setIsGenerating(false);
    }, 3000);
  };

  const handleVideoSelect = (videoUrl: string) => {
    setGeneratedVideo(videoUrl);
  };

  const handlePromptChange = (prompt: string) => {
    setVideoSettings({ ...videoSettings, prompt });
  };

  const handleTemplateSelect = (template: typeof templates[0]) => {
    const newSettings: any = { 
      ...videoSettings, 
      templateName: template.name
    };
    
    // If template specifies a model, use it
    if (template.model) {
      newSettings.model = template.model;
    }
    
    setVideoSettings(newSettings);
  };

  // Filter templates based on search
  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
    template.prompt.toLowerCase().includes(templateSearch.toLowerCase())
  );

  return (
    <div className="image-to-video-page">
      {/* Background Video Container */}
      <div className="background-video-container">
        <div className="background-video"></div>
        <div className="background-overlay"></div>
      </div>
      
      {/* Header */}
      <div className="header-wrapper">
        <header className="face-swap-header">
          <div className="header-container">
            <div className="header-content">
              <Link to="/" className="logo-link">
                <Logo />
              </Link>
              
              {/* Main Navigation Menu */}
              <nav className="main-nav">
                <Link to="/face-swap" className="nav-link">
                  Face Swap
                </Link>
                <Link to="/image-to-video" className="nav-link active">
                  Video Gen
                </Link>
              </nav>
              
              <nav className="header-nav">
                <Link to="/pricing" className="nav-link">
                  Pricing
                </Link>
                
                {isLoading ? (
                  <div className="login-button loading-state">
                    <span className="loading-dots">···</span>
                  </div>
                ) : isLoggedIn ? (
                  <UserProfile />
                ) : (
                  <Link to="/login" className="login-button">
                    Log in
                  </Link>
                )}
              </nav>
            </div>
          </div>
        </header>
      </div>

      {/* Main Content - Three Column Layout */}
      <main className="video-workspace">
        <div className="workspace-container">
          {/* Left Panel - Video Editor */}
          <div className="editor-panel">
            <VideoEditor 
              onGenerate={handleVideoGenerate}
              isGenerating={isGenerating}
              settings={videoSettings}
              onPromptChange={handlePromptChange}
            />
          </div>

          {/* Center Panel - Video Preview */}
          <div className="preview-panel">
            <VideoPreview 
              videoUrl={generatedVideo}
              isGenerating={isGenerating}
              settings={videoSettings}
            />
          </div>

          {/* Right Panel - History */}
          <div className="history-panel">
            <VideoHistory 
              onVideoSelect={handleVideoSelect}
            />
          </div>
        </div>

        {/* Templates Section - Below Workspace */}
        <div className="templates-section">
          <div className="templates-container">
            <div className="templates-header">
              <span className="templates-label">TEMPLATES</span>
              <div className="template-search-container">
                <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  className="template-search-input"
                  placeholder="Search templates..."
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                />
                {templateSearch && (
                  <button 
                    className="search-clear-btn"
                    onClick={() => setTemplateSearch('')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="templates-scroll" ref={templatesRef}>
              {filteredTemplates.length === 0 ? (
                <div className="no-templates-message">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p>No templates found</p>
                  <span>Try a different search term</span>
                </div>
              ) : (
                filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="template-card"
                    onClick={() => handleTemplateSelect(template)}
                  >
                  <div className={`template-thumbnail template-${template.pattern}`}>
                    {template.isVideo ? (
                      <video 
                        src={template.video} 
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <img src={template.image} alt={template.name} />
                    )}
                    {template.isNew && (
                      <div className="template-badge">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                        <span>New</span>
                      </div>
                    )}
                    <div className="template-overlay">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 5v14l11-7z" fill="white" fillOpacity="0.9"/>
                      </svg>
                    </div>
                    <div className="template-name-overlay">{template.name}</div>
                  </div>
                </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ImageToVideoPage;

